// File: src/components/admin/scheduler/CreateScheduleDialog.js
// Dialog for admins to create a new schedule for a user
// Enhanced with trigger configuration and AI analysis task selector

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    FormHelperText,
    Switch,
    FormControlLabel,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import ScheduleBuilder from './ScheduleBuilder';
import { adminApi } from '../../../services/adminApi';

// Schedule types
const SCHEDULE_TYPES = [
    { value: 'PLATFORM_SYNC', label: 'Platform Sync', description: 'Sync campaigns with ad platform (optionally includes stats refresh)' },
    { value: 'AI_ANALYSIS', label: 'AI Analysis', description: 'Run AI analysis on campaigns' },
];

// Intervals
const INTERVALS = [
    { value: '1h', label: 'Every hour' },
    { value: '6h', label: 'Every 6 hours' },
    { value: '12h', label: 'Every 12 hours' },
    { value: '24h', label: 'Daily' },
    { value: '7d', label: 'Weekly' },
];

const CreateScheduleDialog = ({ open, onClose, onSubmit, loading, userUid }) => {
    const [formData, setFormData] = useState({
        type: 'PLATFORM_SYNC',
        platformId: 'google-ads',
        accountId: '',
        interval: '6h',
        enabled: true,
        config: {
            syncDirection: 'pull',
            entityTypes: ['campaigns', 'adGroups', 'keywords', 'ads'],
        },
        // Trigger configuration (optional)
        triggerType: 'schedule',
        eventTriggers: [],
        conditionRules: [],
        delayAfterTrigger: '',
        scheduleInterval: '6h',
        // Execution guards (optional)
        audienceRules: [],
        quietHoursStart: '',
        quietHoursEnd: '',
    });
    const [error, setError] = useState(null);
    const [analysisTasks, setAnalysisTasks] = useState([]);

    // Load analysis tasks when AI_ANALYSIS type is selected
    useEffect(() => {
        if (formData.type === 'AI_ANALYSIS' && analysisTasks.length === 0) {
            adminApi.getAnalysisTasks()
                .then(data => setAnalysisTasks(data.tasks || []))
                .catch(() => { /* ignore */ });
        }
    }, [formData.type, analysisTasks.length]);

    const handleChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        setError(null);
    };

    const setField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(null);
    };

    const handleSubmit = async () => {
        // Validate
        if (!formData.type) {
            setError('Please select a schedule type');
            return;
        }
        if (!formData.interval && formData.triggerType !== 'event') {
            setError('Please select an interval');
            return;
        }

        // Build submit data
        const submitData = {
            type: formData.type,
            platformId: formData.platformId,
            accountId: formData.accountId,
            interval: formData.interval,
            enabled: formData.enabled,
            config: { ...formData.config },
            triggerType: formData.triggerType,
            eventTriggers: formData.eventTriggers,
            conditionRules: formData.conditionRules,
            delayAfterTrigger: formData.delayAfterTrigger || null,
        };

        // Add execution guards if configured
        if (formData.audienceRules?.length > 0) {
            submitData.audienceRules = formData.audienceRules;
        }
        if (formData.quietHoursStart) {
            submitData.quietHoursStart = formData.quietHoursStart;
            submitData.quietHoursEnd = formData.quietHoursEnd;
        }

        try {
            await onSubmit(submitData);
            // Reset form on success
            setFormData({
                type: 'PLATFORM_SYNC',
                platformId: 'google-ads',
                accountId: '',
                interval: '6h',
                enabled: true,
                config: {
                    syncDirection: 'pull',
                    entityTypes: ['campaigns', 'adGroups', 'keywords', 'ads'],
                },
                triggerType: 'schedule',
                eventTriggers: [],
                conditionRules: [],
                delayAfterTrigger: '',
                scheduleInterval: '6h',
                audienceRules: [],
                quietHoursStart: '',
                quietHoursEnd: '',
            });
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to create schedule');
        }
    };

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const selectedType = SCHEDULE_TYPES.find(t => t.value === formData.type);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth data-testid="create-schedule-dialog">
            <DialogTitle>Create Schedule</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Creating schedule for user: <strong>{userUid}</strong>
                    </Typography>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Schedule Type</InputLabel>
                        <Select
                            value={formData.type}
                            onChange={handleChange('type')}
                            label="Schedule Type"
                            data-testid="create-schedule-select-type"
                        >
                            {SCHEDULE_TYPES.map(type => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {selectedType && (
                            <FormHelperText>{selectedType.description}</FormHelperText>
                        )}
                    </FormControl>

                    {formData.type === 'PLATFORM_SYNC' && (
                        <>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Platform</InputLabel>
                                <Select
                                    value={formData.platformId}
                                    onChange={handleChange('platformId')}
                                    label="Platform"
                                    data-testid="create-schedule-select-platform"
                                >
                                    <MenuItem value="google-ads">Google Ads</MenuItem>
                                    <MenuItem value="microsoft-ads">Microsoft Ads</MenuItem>
                                    <MenuItem value="meta-ads" disabled>Meta Ads (Coming Soon)</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Account ID (Optional)"
                                value={formData.accountId}
                                onChange={handleChange('accountId')}
                                placeholder="Leave empty for all accounts"
                                helperText="Specific account ID to sync, or leave empty to sync all connected accounts"
                                sx={{ mb: 2 }}
                                data-testid="create-schedule-input-account-id"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.config?.includeStatsRefresh || false}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            config: { ...prev.config, includeStatsRefresh: e.target.checked },
                                        }))}
                                        data-testid="create-schedule-switch-include-stats"
                                    />
                                }
                                label="Include stats refresh after sync"
                                sx={{ mb: 1, display: 'block' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                When enabled, performance stats will be refreshed after each sync completes.
                            </Typography>

                            {formData.config?.includeStatsRefresh && (
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Stats Periods</InputLabel>
                                    <Select
                                        multiple
                                        value={formData.config?.periods || ['7d', '30d']}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            config: { ...prev.config, periods: e.target.value },
                                        }))}
                                        label="Stats Periods"
                                        data-testid="create-schedule-select-periods"
                                    >
                                        <MenuItem value="7d">Last 7 days</MenuItem>
                                        <MenuItem value="30d">Last 30 days</MenuItem>
                                        <MenuItem value="90d">Last 90 days</MenuItem>
                                    </Select>
                                    <FormHelperText>Select which time periods to refresh stats for</FormHelperText>
                                </FormControl>
                            )}
                        </>
                    )}

                    {/* AI Analysis: Task selector */}
                    {formData.type === 'AI_ANALYSIS' && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Analysis Task</InputLabel>
                            <Select
                                value={formData.config?.taskId || ''}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    config: { ...prev.config, taskId: e.target.value },
                                }))}
                                label="Analysis Task"
                                data-testid="create-schedule-select-analysis-task"
                            >
                                <MenuItem value=""><em>None (use default)</em></MenuItem>
                                {analysisTasks.map(task => (
                                    <MenuItem key={task.id} value={task.id}>
                                        {task.name} ({task.status})
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>Select which AI analysis task configuration to use</FormHelperText>
                        </FormControl>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Interval</InputLabel>
                        <Select
                            value={formData.interval}
                            onChange={handleChange('interval')}
                            label="Interval"
                            data-testid="create-schedule-select-interval"
                        >
                            {INTERVALS.map(interval => (
                                <MenuItem key={interval.value} value={interval.value}>
                                    {interval.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.enabled}
                                onChange={handleChange('enabled')}
                                data-testid="create-schedule-switch-enabled"
                            />
                        }
                        label="Enabled"
                        sx={{ mb: 2 }}
                    />

                    {/* Trigger Configuration (expandable) */}
                    <Accordion sx={{ mb: 1 }} data-testid="create-schedule-trigger-config">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="create-schedule-trigger-config-toggle">
                            <Typography variant="subtitle2">Trigger Configuration</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ScheduleBuilder formData={formData} setField={setField} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Quiet Hours (expandable) */}
                    <Accordion data-testid="create-schedule-quiet-hours">
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="create-schedule-quiet-hours-toggle">
                            <Typography variant="subtitle2">Quiet Hours</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label="Quiet Hours Start"
                                    type="time"
                                    value={formData.quietHoursStart || ''}
                                    onChange={(e) => setField('quietHoursStart', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                    data-testid="create-schedule-input-quiet-start"
                                />
                                <TextField
                                    label="Quiet Hours End"
                                    type="time"
                                    value={formData.quietHoursEnd || ''}
                                    onChange={(e) => setField('quietHoursEnd', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                    data-testid="create-schedule-input-quiet-end"
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Executions will be skipped during quiet hours (based on user timezone).
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading} data-testid="create-schedule-btn-cancel">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} />}
                    data-testid="create-schedule-btn-submit"
                >
                    Create Schedule
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateScheduleDialog;
